<template>
    <div class="sim-list">
        <div class="batch_btn_wrapper">
            <div class="batch_btn_item filters" :key="Number(isMobile)">
                <FilterSelector
                    :firstLevelSelectKeys="firstLevelSelectKeys"
                    :filter_first_current="filterFirstCurrent"
                    :filter_second_current="filterSecondCurrent"
                    :selectorStyle="selectorStyle"
                    :resourceSelectorStyle="resourceSelectorStyle"
                    :subOptions="subOptions"
                    @filter-first="handleFilterFirst"
                    @filter-second="handleFilterSecond"
                />
            </div>

            <router-link to="/user/subscribe-plan/sim/select-sim" class="batch_btn_item add_link">
                <i class="icon icon-co_add"/>
                <span>{{ $t('dashboard.add_card') }}</span>
            </router-link>

            <div class="batch_btn_item batch_btn" v-show="showBatchBtn" @click="isBatchProcess=!isBatchProcess">
                <reo-checkbox v-model="isBatchProcess" id="batch_check"/>
                <span class="batch_text" for="batch_check">{{ $t('devices.batch_process') }}</span>
            </div>
        </div>

        <div class="cards" >
            <SimInfoCard
                v-for="simInfo in filteredShowCardList"
                :key="simInfo.data.simCard?.iccid"
                :select="isBatchProcess"
                :simManager="simInfo"
                :subDevices="subDevices"
                :devices="devices"
                :identifier="infiniteIdentifier"
                :batchCondition="batchCondition"
                :isChecked="simInfo.selected"
                @change="value=>selectSim(simInfo,value)"
            />
            <infiniteLoading
                @infinite="infiniteHandler"
                :key="infiniteIdentifier"
                spinner="spiral"
                class="loading"
            >
                <div slot="spinner">
                    <div class="processing-container">
                        <processing/>
                    </div>
                </div>
                <div slot="no-more">
                    <no-more/>
                </div>
                <div slot="no-results">
                    <no-sim/>
                </div>
                <div slot="error">
                    <no-sim/>
                </div>
            </infiniteLoading>
        </div>
        <div class="batch_apply_btn" v-show="isBatchProcess">
            <ReoCommonButton
                size="large"
                :label="$t('dashboard.renew')"
                :customWidth="isMobile?'100%':'253'"
                :disabled="selectedSimList.length === 0"
                @click="applyBatchProcess"
            />
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import SimInfoCard from '@/components/SimInfoCard.vue';
import reoCheckbox from '@/components/reoCheckbox.vue';
import NoSim from '@/components/NoSim.vue';
import { getSimCardList } from '@/api/sim';
import {
    ESimCardModule, getPlanFilter, IOriginalPlanFilter,
    IPlanFilter,
    IReolinkDevice,
    IReoSelectOption,
    ISelectorStyle,
    ISubDevice,
    IGetSimCardList_BodyArgs,
} from '@reolink-cloud/interface';
import { SimManager } from '@/lib/simManager';
import { mapGetters } from 'vuex';
import { productsManager } from '@/lib';
import processing from '@/basic/processing.vue';
import InfiniteLoading, { StateChanger } from 'vue-infinite-loading';
import NoMore from '@/view/library/NoMore.vue';
import { unfoldError } from '@/common/js/notice';
import nprogress from 'nprogress';
import { uidHandler } from '@/lib/uidHandler';
import { getSubDeviceList } from '@/api/subscriptions';
import { getDeviceList } from '@/api/cloud';
import { errorUploader } from '@/lib/errorUploader';

export default Vue.extend({
    name: 'SimList',
    components: {
        SimInfoCard,
        reoCheckbox,
        NoSim,
        InfiniteLoading,
        processing,
        NoMore,
    },
    data() {

        let filterFirstCurrent: IPlanFilter = 'all';
        let filterSecondCurrent: IPlanFilter = 'all';

        const filter_first = this.$route.query['filter_first'] ?? 'all';
        if ( typeof filter_first === 'string' ) {

            filterFirstCurrent = getPlanFilter( filter_first as IOriginalPlanFilter );

        }
        let filter_second = this.$route.query['filter_second'];
        if ( typeof filter_second === 'string' ) {

            if ( filter_second === 'all' ) {

                filter_second = filter_first;

            }
            filterSecondCurrent = getPlanFilter( filter_second as IOriginalPlanFilter );

        }
        return {
            firstLevelSelectKeys:[
                { label: this.$t( 'devices.filter.all' ).toString(), value: 'all', id: 'all' },
                { label: this.$t( 'devices.filter.no_or_expired.title' ).toString(), value: 'inactive', id: 'no_or_expired' },
                { label: this.$t( 'devices.filter.subscription_active.title' ).toString(), value: 'active', id: 'active' },
                { label: this.$t( 'devices.filter.unbound' ).toString(), value: 'unbound', id: 'unbound' }
            ] as IReoSelectOption[],
            filterFirstCurrent,
            filterSecondCurrent,
            simInfoList: [] as SimManager[],
            showCardList: [] as SimManager[],
            isBatchProcess: false,
            pageInfo: {
                page: 1,
                limit: 30,
                total: 1,
            },
            infiniteIdentifier: 1,
            requestVersion: 0,
            subDevices: [] as ISubDevice[],
            devices: [] as IReolinkDevice[],
            /** 与 SIM 列表一并拉取设备链数据，仅首屏一次；供卡片展示及 shouldDisplaySimByDeviceFilter */
            listDependenciesLoaded: false,
            /** 串行化 infinite 回调，避免并行请求同一 page 导致列表重复 */
            simListInfiniteChain: Promise.resolve() as Promise<void>,
        };

    },
    computed: {
        ...mapGetters<'lang' | 'isMobile'>( ['lang', 'isMobile'] ),
        hasSimCard(): boolean {

            return this.simInfoList.length > 0;

        },
        showBatchBtn(): boolean {

            return this.hasSimCard;

        },
        batchCondition(): string | undefined {

            const selectedList = this.simInfoList.filter( item => item.selected );
            if ( selectedList.length > 0 ) {

                return selectedList[0].batchCondition;

            }
            return undefined;

        },
        selectedSimList(): SimManager[] {

            return this.filteredShowCardList.filter( item => item.selected );

        },
        subOptions(): IReoSelectOption[]|null {

            if ( this.filterFirstCurrent === 'inactive' ) {

                return [
                    { label: this.$t( 'devices.filter.all' ).toString(), value: 'inactive', id: 'all', selected: true },
                    { label: this.$t( 'devices.filter.no_or_expired.sub_options.add_on_active' ).toString(), value: 'inactive_activeDataPack', id: 'add_on_active' },
                ];

            } else if ( this.filterFirstCurrent === 'active' ) {

                return [
                    { label: this.$t( 'devices.filter.all' ).toString(), value: 'active', id: 'all', selected: true },
                    { label: this.$t( 'devices.filter.subscription_active.sub_options.expired_soon_prefer' ).toString(), value: 'active_expiringSoon', id: 'expired_soon_prefer' },
                    { label: this.$t( 'devices.filter.subscription_active.sub_options.traffic_run_out' ).toString(), value: 'active_dataDepleted', id: 'traffic_run_out' },
                    { label: this.$t( 'devices.filter.subscription_active.sub_options.traffic_run_out_soon' ).toString(), value: 'active_dataRemaining', id: 'traffic_run_out_soon' },
                ];

            } else {

                return null;

            }

        },
        isMobile (): boolean {

            return  this.$store.getters.isMobile;

        },
        currentFilter(): IPlanFilter {

            if ( this.filterSecondCurrent === 'all' ) {

                return this.filterFirstCurrent;

            } else {

                return this.filterSecondCurrent;

            }

        },
        selectorStyle (): ISelectorStyle {

            return !this.isMobile ? {
                widthOut: 1,
                heightOut: 0.48,
                widthIn: 1,
                heightIn: 0.48,
                selectMarginLeft: 0.16,
                triangleRight: 0.16,
                maxHeight: 3.08,
                fontSizeIn: 0.16,
                fontSizeOut: 0.14
            } : {
                widthOut: -1,
                heightOut: 0.36,
                widthIn: -1,
                heightIn: 0.36,
                selectMarginLeft: 0.12,
                triangleRight: 0.12,
                maxHeight: 4.75,
                fontSizeIn: 0.17,
                fontSizeOut: 0.13
            };

        },
        resourceSelectorStyle(): ISelectorStyle {

            return !this.isMobile ? {
                ...this.selectorStyle,
                widthOut: 3.2,
                widthIn: 3.2
            } : this.selectorStyle;

        },

        /** 列表数据由 list-query 的 planFilter 筛选；此处仅处理批量续费时的可选中范围 */
        filteredShowCardList(): SimManager[] {

            if ( !this.isBatchProcess ) {

                return this.simInfoList;

            }

            return this.simInfoList.filter(
                item => item.renewAble &&
                    ( this.batchCondition === undefined || item.batchCondition === this.batchCondition )
            );

        }
    },
    methods: {
        resetState() {

            this.pageInfo.page = 1;
            this.pageInfo.total = 1;
            this.simInfoList = [];
            this.showCardList = [];
            this.requestVersion++;
            this.infiniteIdentifier++;
            this.simListInfiniteChain = Promise.resolve();

        },
        async ensureSimListDependencies( version: number ): Promise<void> {

            if ( this.listDependenciesLoaded ) {

                return;

            }
            try {

                this.subDevices = await getSubDeviceList();
                if ( this.requestVersion !== version ) return;
                this.devices = await getDeviceList();
                if ( this.requestVersion !== version ) return;
                await productsManager.update({ lang: this.$i18n.locale });

            } catch ( e ) {

                console.error( e );
                errorUploader.uploadCommonError( 'SimList_ensureSimListDependencies', e );

            } finally {

                if ( this.requestVersion === version ) {

                    this.listDependenciesLoaded = true;

                }

            }

        },
        /** 与原先 computed 一致：已解绑且（无生效套餐或套餐未生效）的卡不进入列表 */
        shouldDisplaySimByDeviceFilter( simInfo: SimManager ): boolean {

            const boundStatus = this.devices.find( d => d.uid === simInfo.data.esimDevice )?.status;

            return !( boundStatus === 'unbound' && ( simInfo.currentSubscription?.effectiveStatus === 'inactive' || !simInfo.currentSubscription ));

        },
        async getSimList( version: number ): Promise<void> {

            await this.ensureSimListDependencies( version );
            if ( this.requestVersion !== version ) return;

            const listParams: IGetSimCardList_BodyArgs = {
                includes: [
                    ESimCardModule.CLOUD_SERVICES,
                    ESimCardModule.LAST_USED_DEVICE,
                    ESimCardModule.SIM_CARD
                ],
                limit: this.pageInfo.limit,
                page: this.pageInfo.page,
                sortStrategy: 'simCardList',
                lang: this.$i18n.locale,
                planFilter: this.currentFilter
            };

            const getSimCardRes = await getSimCardList( listParams );
            if ( this.requestVersion !== version ) return;
            const mapped = getSimCardRes.items.map( item => new SimManager( item, getSimCardRes.services ));
            const simInfoList = mapped.filter( item => this.shouldDisplaySimByDeviceFilter( item ));
            const newSimInfoList  = [...this.simInfoList, ...simInfoList ];
            // 在 initSimManagers 前检查版本，避免过期请求执行不必要的初始化
            if ( this.requestVersion !== version ) return;
            await SimManager.initSimManagers( newSimInfoList );
            if ( this.requestVersion !== version ) return;
            this.simInfoList = newSimInfoList;
            this.pageInfo.page++;
            this.pageInfo.total = getSimCardRes.totalRows;

        },
        async infiniteHandler( $state: StateChanger ) {

            const version = this.requestVersion;

            const run = async (): Promise<void> => {

                const pageNum = Math.ceil( this.pageInfo.total / this.pageInfo.limit );
                if ( this.pageInfo.page > pageNum ) {

                    $state.complete();
                    return;

                }

                try {

                    nprogress.start();
                    await this.getSimList( version );

                } catch ( e ) {

                    if ( this.requestVersion !== version ) return;
                    console.error( e );
                    errorUploader.uploadCommonError( 'SimList_infiniteHandler', e );
                    unfoldError( 'errors.server_error' );
                    $state.error();
                    return;

                } finally {

                    // 只有当前版本的请求才结束进度条，避免过期请求提前关闭
                    if ( this.requestVersion === version ) {

                        nprogress.done();

                    }

                }

                if ( this.requestVersion !== version ) {

                    // infiniteIdentifier 已随 resetState() 自增，vue-infinite-loading 会重置组件状态
                    // 旧的 $state 不再有效，无需通知，直接退出
                    return;

                }
                const pageNumAfter = Math.ceil( this.pageInfo.total / this.pageInfo.limit );
                if ( this.pageInfo.page > pageNumAfter ) {

                    // vue-infinite-loading：首次 complete 且从未 loaded 会走 no-results；有数据时需先 loaded 再 complete 才显示 no-more
                    if ( this.simInfoList.length > 0 ) {

                        $state.loaded();

                    } else {

                        $state.complete();

                    }

                } else {

                    $state.loaded();

                }

            };

            this.simListInfiniteChain = this.simListInfiniteChain.then( run, run );
            await this.simListInfiniteChain;

        },
        refresh() {

            this.pageInfo.page = 1;
            this.pageInfo.total = 1;
            this.simInfoList = [];
            this.infiniteIdentifier++;
            this.simListInfiniteChain = Promise.resolve();
            window.scrollTo({ top: window.scrollY + 1 });
            window.scrollTo({ top: window.scrollY - 1 });

        },
        async applyBatchProcess() {

            if ( this.selectedSimList.length === 0 ) {

                return;

            }
            await this.$router.push({
                path: '/checkout/',
                query: {
                    p_id: this.selectedSimList.map( sim => sim.currentSubscription?.plan ?? '0' ),
                    ps_id: this.selectedSimList.map( sim => sim.currentSubscription?.id ?? '0' ),
                    ymh: this.selectedSimList.map( sim => uidHandler.encrypt( sim.iccid )),
                    mode: 'renew'
                }
            });

        },
        selectSim( simInfo: SimManager, value: boolean ) {

            if ( this.selectedSimList.length >= 20 && value ) {

                unfoldError( 'errors.exceed_batch_limit', 10000 );

            } else {

                simInfo.selected = value;

            }

        },

        handleFilterFirst( select:  IPlanFilter ) {

            this.filterFirstCurrent = select;

            if ( select === 'inactive' || select === 'active' ) {

                this.filterSecondCurrent = select;

            } else {

                this.filterSecondCurrent = 'all';

            }
            this.resetState();

        },
        handleFilterSecond( select:  IPlanFilter ) {

            this.filterSecondCurrent = select;
            this.resetState();

        }
    },
});
</script>

<style lang="less" scoped>
.sim-list {
    margin-bottom: 100px;
    min-height: 532px;
    @media @mobile {
        margin-bottom: 0;
    }

    .batch_btn_wrapper {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-wrap: wrap;
        margin: 17px 0;

        @media @mobile{
            justify-content: space-between;
        }

        .add_link, filters{
            margin-right: auto;
        }
        .batch_btn{
            margin-left: auto;
        }

        .filters{
            display: flex;
            justify-content: flex-start;
            gap: 26px;
            @media @mobile{
                width:100%;
                flex-direction: column;
                gap: 10px;
                margin-bottom: 16px;
            }

            .reo-select{
                min-width:350px;
            }

            .reo-sub-select{
                min-width:350px;
            }
        }

        .add_link {
            color: @b_6;
            display: inline-flex;
            align-items: center;

            .icon {
                font-size: 18px;
            }

            span {
                font-size: 14px;
                font-weight: 500;
            }
        }

        .batch_btn {
            display: inline-flex;
            align-items: center;
            font-family: @font_02;
            color: @font_primary;
            font-size: 16px;
            font-weight: normal;
            line-height: normal;
            letter-spacing: 0em;
            cursor: pointer;
            user-select: none;

            .batch_text {
                margin-left: 8px;
                font-size: 16px;
                line-height: 16px;

                @media @mobile {
                    font-size: 14px;
                    line-height: 14px;
                }
            }
        }
    }

    .cards {

        display: flex;
        gap: 16px;
        flex-wrap: wrap;
    }

    .batch_apply_btn {
        display: flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        bottom: 0;
        left: 0;
        height: 120px;
        background-color: @bg_02;
        width: 100%;
        z-index: 8;
        box-shadow: 0px -2px 8px 0px rgba(0, 0, 0, 0.04);
        @media @mobile {
            height: 108px;
            align-items: flex-start;
            padding: 19px 24px 0px;
            padding-left: 24px;
            padding-right: 24px;
            padding-top: 19px;
            box-sizing: border-box;
            border-top: 1px solid @line;
            box-shadow: none;
        }
    }

    .processing-container {
        padding-top: 100px;
        height: 100px;
    }

    .loading {
        width: 100%;

        /deep/ .no-more {
            margin-top: -16px;
            padding: 24px 0;
        }
    }
}
</style>
